/**
 * Lead inbox queries and mutations.
 *
 * Reads go straight to Supabase under the `crm_lead_inbox_select` RLS policy —
 * which is also what keeps `suppressed` rows off this surface without the page
 * having to know they exist. Writes go through the service-role endpoints,
 * because the table grants `authenticated` nothing but SELECT and no
 * INSERT/UPDATE policy is being added.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { sanitizeIlikeTerm } from '@/lib/crm/query-filters';
import {
  crmKeys,
  resolvedScopeKey,
  useCrmQueryScope,
  type CrmQueryScope,
} from '@/hooks/crm/useContacts';
import type { LeadInboxReviewState, LeadInboxRow } from '@/lib/crm/lead-inbox';

/** The queue is meant to stay small; a page this size has never been reached. */
const LEAD_INBOX_PAGE_SIZE = 100;

/** The states an operator browses. `suppressed` is invisible by policy. */
export const BROWSABLE_STATES = ['pending', 'snoozed', 'approved', 'rejected'] as const;

export type BrowsableState = (typeof BROWSABLE_STATES)[number];

export const leadInboxKeys = {
  all: ['lead-inbox'] as const,
  list: (scope: CrmQueryScope, state: BrowsableState, search: string) =>
    [...leadInboxKeys.all, 'list', ...scope, state, search] as const,
  counts: (scope: CrmQueryScope) => [...leadInboxKeys.all, 'counts', ...scope] as const,
};

// The two helpers below mirror `src/hooks/use-acquisition-signals.ts`. Both
// surfaces authenticate the same way on purpose — a second auth idiom is how
// the LinkedIn analytics 401 happened.
const accessToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
};

const apiError = async (response: Response): Promise<Error> => {
  const body = await response.json().catch(() => ({})) as {
    error?: { message?: string; code?: string };
    message?: string;
  };
  const error = new Error(body.error?.message ?? body.message ?? `API error: ${response.status}`);
  error.name = body.error?.code ?? 'ApiError';
  return error;
};

// ═══════════════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════════════

export function useLeadInbox(state: BrowsableState, search: string) {
  const scope = useCrmQueryScope();

  return useQuery<LeadInboxRow[]>({
    queryKey: leadInboxKeys.list(resolvedScopeKey(scope), state, search),
    queryFn: async () => {
      let query = supabase
        .from('crm_lead_inbox')
        .select('*')
        .eq('organization_id', scope![1])
        .eq('review_state', state)
        .order('lead_score', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(LEAD_INBOX_PAGE_SIZE);

      // PostgREST's `or(...)` grammar treats `,()` as structural separators, so
      // raw operator input goes through the shared sanitiser first.
      const term = sanitizeIlikeTerm(search);
      if (term) {
        const pattern = `%${term}%`;
        query = query.or(
          `first_name.ilike.${pattern},last_name.ilike.${pattern},company_name.ilike.${pattern},email.ilike.${pattern}`,
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as LeadInboxRow[];
    },
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

export type LeadInboxCounts = Record<BrowsableState, number>;

export function useLeadInboxCounts() {
  const scope = useCrmQueryScope();

  return useQuery<LeadInboxCounts>({
    queryKey: leadInboxKeys.counts(resolvedScopeKey(scope)),
    queryFn: async () => {
      const entries = await Promise.all(
        BROWSABLE_STATES.map(async (state) => {
          const { count, error } = await supabase
            .from('crm_lead_inbox')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', scope![1])
            .eq('review_state', state);

          if (error) throw error;
          return [state, count ?? 0] as const;
        }),
      );

      return Object.fromEntries(entries) as LeadInboxCounts;
    },
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════════════

export interface ReviewLeadInput {
  id: string;
  decision: 'approve' | 'reject' | 'snooze';
  note?: string | null;
  snoozeUntil?: string | null;
  name?: string | null;
  company?: string | null;
}

export interface ReviewLeadResponse {
  success: true;
  lead: {
    id: string;
    reviewState: LeadInboxReviewState;
    contactId: string | null;
    promotedAt: string | null;
    dueDate: string | null;
  };
}

export function useReviewLead() {
  const queryClient = useQueryClient();

  return useMutation<ReviewLeadResponse, Error, ReviewLeadInput>({
    mutationFn: async (input) => {
      const token = await accessToken();
      const response = await fetch('/api/crm/lead-inbox-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw await apiError(response);
      return response.json() as Promise<ReviewLeadResponse>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leadInboxKeys.all });
      // An approval created a contact, so the cockpit's counts are now stale.
      await queryClient.invalidateQueries({ queryKey: crmKeys.all });
    },
  });
}

export interface ImportRejectionSummary {
  line: number;
  reason: string;
  value: string;
}

export interface ImportSummary {
  parsedRows: number;
  candidates: number;
  imported: number;
  suppressedExistingContact: number;
  alreadyInInbox: number;
  duplicateInFile: number;
  rejectedCount: number;
  rejected: ImportRejectionSummary[];
}

export interface ImportLeadCsvResponse {
  success: true;
  dryRun: boolean;
  importRunId: string | null;
  summary: ImportSummary;
}

export interface ImportLeadCsvInput {
  csv: string;
  fileName: string;
  dryRun: boolean;
}

export function useImportLeadCsv() {
  const queryClient = useQueryClient();

  return useMutation<ImportLeadCsvResponse, Error, ImportLeadCsvInput>({
    mutationFn: async (input) => {
      const token = await accessToken();
      const response = await fetch('/api/crm/lead-inbox-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw await apiError(response);
      return response.json() as Promise<ImportLeadCsvResponse>;
    },
    onSuccess: async (result) => {
      // A dry run changed nothing; refetching would only flicker the queue.
      if (result.dryRun) return;
      await queryClient.invalidateQueries({ queryKey: leadInboxKeys.all });
    },
  });
}
